import { type FormEvent, useCallback, useEffect, useState } from 'react'
import { useLocale } from '../../shared/i18n/LocaleContext'
import {
  createAplConfiguration,
  executeAplConfigurationAction,
  getAplConfigurations,
  type AplConfigurationAction,
  type CreateAplConfigurationRequest,
} from './aplPanelApi'
import type { AplConfigurationSummary } from './types'
import { supportedLocales } from '../../shared/i18n/translations'
import { Link, useSearchParams } from 'react-router-dom'
import { AppModal } from '../../shared/components/AppModal'
import { useAppConfirm } from '../../shared/components/AppConfirmDialog'
import { PasswordInput } from '../../shared/components/PasswordInput'
import { SortableHeaders, useUrlSortedRows } from '../../shared/components/SortableTableHeader'
import { AplAnnouncementHistory, AplAnnouncementModal } from './AplAnnouncementsPage'

const emptyCreateRequest: CreateAplConfigurationRequest = {
  code: '',
  name: '',
  defaultCulture: 'es-ES',
  companyCode: '',
  companyName: '',
  administratorUserName: '',
  administratorDisplayName: '',
  temporaryPassword: '',
  administratorLicenseQuantity: 5,
}

export function AplConfigurationsPage() {
  const { t, formatDateTime, formatNumber, locale } = useLocale()
  const { confirm, confirmationDialog } = useAppConfirm()
  const [searchParams, setSearchParams] = useSearchParams()
  const [configurations, setConfigurations] = useState<AplConfigurationSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [activeActionId, setActiveActionId] = useState<string | null>(null)
  const [actionMessage, setActionMessage] = useState<string | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [announcementTarget,setAnnouncementTarget]=useState<string|null|undefined>(undefined)
  const [announcementRefresh,setAnnouncementRefresh]=useState(0)
  const [createRequest, setCreateRequest] =
    useState<CreateAplConfigurationRequest>(emptyCreateRequest)

  const loadConfigurations = useCallback(() => {
    const controller = new AbortController()
    setIsLoading(true)
    setHasError(false)
    getAplConfigurations(controller.signal)
      .then(setConfigurations)
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          setHasError(true)
        }
      })
      .finally(() => setIsLoading(false))

    return controller
  }, [])

  useEffect(() => {
    const controller = loadConfigurations()
    return () => controller.abort()
  }, [loadConfigurations])
  useEffect(() => setShowCreateForm(searchParams.get('new') === '1'), [searchParams])
  const closeCreate = () => { setShowCreateForm(false); const next = new URLSearchParams(searchParams); next.delete('new'); setSearchParams(next, { replace: true }) }

  const executeAction = async (
    configuration: AplConfigurationSummary,
    action: AplConfigurationAction,
  ) => {
    const confirmationKey = action === 'suspend'
      ? 'aplPanel.confirmSuspend'
      : action === 'reactivate'
        ? 'aplPanel.confirmReactivate'
        : 'aplPanel.confirmRetry'
    if (!await confirm(t(confirmationKey))) {
      return
    }

    setActiveActionId(configuration.id)
    setActionMessage(null)
    try {
      const code = await executeAplConfigurationAction(configuration.id, action)
      setActionMessage(t(code as Parameters<typeof t>[0]))
      loadConfigurations()
    } catch (error) {
      const code = error instanceof Error ? error.message : 'aplPanel.actionError'
      setActionMessage(t(code as Parameters<typeof t>[0]))
    } finally {
      setActiveActionId(null)
    }
  }

  const submitCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!await confirm(t('aplPanel.confirmCreate'))) {
      return
    }

    setActiveActionId('create')
    setActionMessage(null)
    setCreateError(null)
    try {
      const code = await createAplConfiguration(createRequest)
      setActionMessage(t(code as Parameters<typeof t>[0]))
      setCreateRequest(emptyCreateRequest)
      closeCreate()
      loadConfigurations()
    } catch (error) {
      const code = error instanceof Error ? error.message : 'aplPanel.actionError'
      setCreateError(t(code as Parameters<typeof t>[0]))
    } finally {
      setActiveActionId(null)
    }
  }

  const updateCreateField = <Key extends keyof CreateAplConfigurationRequest>(
    key: Key,
    value: CreateAplConfigurationRequest[Key],
  ) => {
    setCreateRequest((current) => ({ ...current, [key]: value }))
  }
  const tableSort=useUrlSortedRows(configurations,{code:x=>x.code,name:x=>x.name,status:x=>x.isSuspended?'Suspended':x.provisioningStatus,
    schema:x=>x.schemaVersion,licenses:x=>x.licenseCount,sessions:x=>x.activeSessionCount,last:x=>x.lastProvisioningAtUtc},'code','asc',locale)

  return (
    <main className="page-content">
      <section className="welcome">
        <div>
          <span className="eyebrow">{t('aplPanel.eyebrow')}</span>
          <h1>{t('aplPanel.configurations')}</h1>
          <p>{t('aplPanel.subtitle')}</p>
        </div>
        <div className="welcome-actions"><button type="button" className="secondary-button" onClick={()=>setAnnouncementTarget(null)}>{t('notifications.newGlobalAnnouncement')}</button><button
          type="button" className="primary-action"
          onClick={() => { setCreateError(null); setSearchParams({ new: '1' }) }}>
          {t('aplPanel.create')}
        </button></div>
      </section>

      {showCreateForm && (
        <AppModal
          title={t('aplPanel.createTitle')}
          closeLabel={t('nav.closeMenu')}
          onClose={closeCreate}
          wide
          extraClassName="engineering-modal"
          feedback={createError}
        >
        <form className="create-configuration-form administration-form modal-form" onSubmit={submitCreate}>
          <p>{t('aplPanel.createSubtitle')}</p>
          <div className="form-grid">
            <label>
              <span>{t('aplPanel.code')}</span>
              <input required value={createRequest.code} onChange={(event) => updateCreateField('code', event.target.value)} />
            </label>
            <label>
              <span>{t('aplPanel.name')}</span>
              <input required value={createRequest.name} onChange={(event) => updateCreateField('name', event.target.value)} />
            </label>
            <label>
              <span>{t('aplPanel.defaultCulture')}</span>
              <select value={createRequest.defaultCulture} onChange={(event) => updateCreateField('defaultCulture', event.target.value)}>
                {supportedLocales.map((locale) => <option key={locale} value={locale}>{locale}</option>)}
              </select>
            </label>
            <label>
              <span>{t('aplPanel.licenseQuantity')}</span>
              <input type="number" min="1" max="10000" required value={createRequest.administratorLicenseQuantity} onChange={(event) => updateCreateField('administratorLicenseQuantity', Number(event.target.value))} />
            </label>
            <label>
              <span>{t('aplPanel.companyCode')}</span>
              <input required value={createRequest.companyCode} onChange={(event) => updateCreateField('companyCode', event.target.value)} />
            </label>
            <label>
              <span>{t('aplPanel.companyName')}</span>
              <input required value={createRequest.companyName} onChange={(event) => updateCreateField('companyName', event.target.value)} />
            </label>
            <label>
              <span>{t('aplPanel.administratorUserName')}</span>
              <input required autoComplete="off" value={createRequest.administratorUserName} onChange={(event) => updateCreateField('administratorUserName', event.target.value)} />
            </label>
            <label>
              <span>{t('aplPanel.administratorDisplayName')}</span>
              <input required value={createRequest.administratorDisplayName} onChange={(event) => updateCreateField('administratorDisplayName', event.target.value)} />
            </label>
            <label className="full-width">
              <span>{t('aplPanel.temporaryPassword')}</span>
              <PasswordInput minLength={12} required autoComplete="new-password" value={createRequest.temporaryPassword} onChange={(event) => updateCreateField('temporaryPassword', event.target.value)} />
              <small>{t('aplPanel.passwordRequirements')}</small>
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" disabled={activeActionId === 'create'}>
              {activeActionId === 'create' ? t('aplPanel.creating') : t('aplPanel.createAction')}
            </button>
          </div>
        </form>
        </AppModal>
      )}

      {isLoading && <div className="panel-message">{t('aplPanel.loading')}</div>}
      {hasError && <div className="panel-message error">{t('aplPanel.loadError')}</div>}
      {actionMessage && <div className="panel-message compact">{actionMessage}</div>}
      {!isLoading && !hasError && configurations.length === 0 && (
        <div className="panel-message">{t('aplPanel.empty')}</div>
      )}

      {!isLoading && !hasError && configurations.length > 0 && (
        <section className="data-panel" aria-label={t('aplPanel.configurations')}>
          <table className="data-table">
            <thead>
              <tr>
                <SortableHeaders sort={tableSort} columns={[["code",t('aplPanel.code')],["name",t('aplPanel.name')],["status",t('aplPanel.status')],["schema",t('aplPanel.schema')],["licenses",t('aplPanel.licenses')],["sessions",t('aplPanel.sessions')],["last",t('aplPanel.lastProvisioning')]]}/>
                <th>{t('aplPanel.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {tableSort.sortedRows.map((configuration) => (
                <tr key={configuration.id}>
                  <td>{configuration.provisioningStatus === 'Active'
                    ? <Link className="record-code-link" to={`/apl/configuraciones/${configuration.id}`}>{configuration.code}</Link>
                    : <strong>{configuration.code}</strong>}</td>
                  <td>
                    {configuration.name}
                    <small>{configuration.defaultCulture}</small>
                  </td>
                  <td>
                    <StatusBadge
                      status={configuration.isSuspended
                        ? 'Suspended'
                        : configuration.provisioningStatus}
                    />
                  </td>
                  <td>{formatNumber(configuration.schemaVersion)}</td>
                  <td>{formatNumber(configuration.licenseCount)}</td>
                  <td>{formatNumber(configuration.activeSessionCount)}</td>
                  <td>
                    {configuration.lastProvisioningAtUtc
                      ? formatDateTime(configuration.lastProvisioningAtUtc)
                      : t('aplPanel.never')}
                    {configuration.lastProvisioningStatus && (
                      <small>{configuration.lastProvisioningStatus}</small>
                    )}
                  </td>
                  <td>
                    <div className="table-actions">
                      <button type="button" onClick={()=>setAnnouncementTarget(configuration.id)}>{t('notifications.newAnnouncement')}</button>
                      {configuration.isSuspended && (
                        <button
                          type="button"
                          disabled={activeActionId === configuration.id}
                          onClick={() => void executeAction(configuration, 'reactivate')}
                        >
                          {t('aplPanel.reactivate')}
                        </button>
                      )}
                      {!configuration.isSuspended &&
                        configuration.provisioningStatus === 'Active' &&
                        configuration.code !== 'APL' && (
                          <button
                            type="button"
                            className="danger"
                            disabled={activeActionId === configuration.id}
                            onClick={() => void executeAction(configuration, 'suspend')}
                          >
                            {t('aplPanel.suspend')}
                          </button>
                        )}
                      {configuration.provisioningStatus === 'Failed' && (
                        <button
                          type="button"
                          disabled={activeActionId === configuration.id}
                          onClick={() => void executeAction(configuration, 'retry')}
                        >
                          {t('aplPanel.retry')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      <AplAnnouncementHistory refreshToken={announcementRefresh}/>
      {announcementTarget!==undefined&&<AplAnnouncementModal configurations={configurations} targetConfigurationId={announcementTarget} onClose={()=>setAnnouncementTarget(undefined)} onSaved={()=>setAnnouncementRefresh(value=>value+1)}/>}
      {confirmationDialog}
    </main>
  )
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useLocale()
  const normalizedStatus = status.toLowerCase()
  const label = normalizedStatus === 'active'
    ? t('aplPanel.statusActive')
    : normalizedStatus === 'failed'
      ? t('aplPanel.statusFailed')
      : normalizedStatus === 'suspended'
        ? t('aplPanel.statusSuspended')
        : normalizedStatus === 'provisioning'
          ? t('aplPanel.statusProvisioning')
          : t('aplPanel.statusPending')

  return <span className={`status-badge ${normalizedStatus}`}>{label}</span>
}
